import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule } from '@ngx-translate/core';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';

export interface CreateDialogKnowledgeSource {
  id: string;
  kind: 'url' | 'pdf' | 'file' | 'custom';
  name: string;
  subtitle?: string;
}

export interface KnowledgeBaseCreateResult {
  name: string;
  description: string;
  sourceIds: string[];
}

export interface KnowledgeBaseCreateDialogData {
  availableSources: CreateDialogKnowledgeSource[];
  /**
   * Persists the new KB. Resolves on success (the dialog then closes); rejects to keep the dialog open with the user's inputs intact while an error snackbar is shown.
   */
  onCreate: (result: KnowledgeBaseCreateResult) => Promise<void>;
}

@Component({
  selector: 'ai-knowledge-base-create-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    GalaxyFormFieldModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
    AiKnowledgeI18nModule,
  ],
  templateUrl: './knowledge-base-create-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./knowledge-base-create-dialog.component.scss'],
})
export class KnowledgeBaseCreateDialogComponent {
  protected readonly data = inject<KnowledgeBaseCreateDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<KnowledgeBaseCreateDialogComponent, KnowledgeBaseCreateResult | undefined>>(MatDialogRef);
  private readonly snackbar = inject(SnackbarService);

  readonly name = signal('');
  readonly description = signal('');
  readonly search = signal('');
  readonly selectedIds = signal<Set<string>>(new Set());
  /** True while `onCreate` is in flight — disables the form so inputs can't be lost mid-request. */
  readonly submitting = signal(false);

  readonly selectedCount = computed(() => this.selectedIds().size);

  readonly filteredSources = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.data.availableSources;
    return this.data.availableSources.filter(
      (ks) => ks.name.toLowerCase().includes(q) || (ks.subtitle?.toLowerCase().includes(q) ?? false),
    );
  });

  isSelected(ks: CreateDialogKnowledgeSource): boolean {
    return this.selectedIds().has(ks.id);
  }

  toggle(ks: CreateDialogKnowledgeSource, checked: boolean): void {
    this.selectedIds.update((set) => {
      const next = new Set(set);
      if (checked) next.add(ks.id);
      else next.delete(ks.id);
      return next;
    });
  }

  cancel(): void {
    if (this.submitting()) return;
    this.dialogRef.close(undefined);
  }

  async create(): Promise<void> {
    if (this.name().trim().length === 0 || this.submitting()) return;
    const result: KnowledgeBaseCreateResult = {
      name: this.name().trim(),
      description: this.description().trim(),
      sourceIds: Array.from(this.selectedIds()),
    };
    this.submitting.set(true);
    try {
      await this.data.onCreate(result);
      this.dialogRef.close(result);
    } catch (err) {
      console.error('[knowledge-management] createKnowledgeBase failed', err);
      this.snackbar.openErrorSnack('KNOWLEDGE_SHARING.CREATE.CREATE_ERROR');
    } finally {
      this.submitting.set(false);
    }
  }

  iconFor(kind: CreateDialogKnowledgeSource['kind']): string {
    switch (kind) {
      case 'url': return 'language';
      case 'pdf': return 'picture_as_pdf';
      case 'file': return 'description';
      case 'custom': return 'notes';
      default: return 'description';
    }
  }

  trackKSId = (_: number, ks: CreateDialogKnowledgeSource): string => ks.id;
}
